/**
 * `/$org/agents`: the agents that work for the organization, on one calm page
 * beside Home's sidebar — Amoreg (the chat agent), Inspector (the review
 * agent), Probe (the interview app) and the organization's automations
 * (scheduled agents).
 *
 * Every figure is read from a store the app already keeps, scoped to the
 * active organization: the chat list, the Inspector's runs, schedule and the
 * findings queue, Probe's saved interviews, and the automations list. A card
 * whose source has not answered says so ("…"); none is estimated.
 *
 * @module components/agents/AgentsPage
 */
import { scopeProjectRef } from "@amoreg/client-runtime/environment";
import { Link } from "@tanstack/react-router";
import { ArrowRightIcon, CalendarClockIcon, RadarIcon, SquarePenIcon } from "lucide-react";
import { useMemo, type ReactElement, type ReactNode } from "react";

import { useNewThreadHandler } from "../../hooks/useHandleNewThread";
import { useNowMinute } from "../../hooks/useNowMinute";
import { cn } from "../../lib/utils";
import { orgUrlSegment } from "../../orgRouting";
import { useAutomations } from "../../state/automations";
import { useProjects, useThreadShells } from "../../state/entities";
import { usePrimaryEnvironmentId } from "../../state/environments";
import { useInspections } from "../../state/inspections";
import { useOrgProfile } from "../../state/orgProfile";
import { useActiveOrg } from "../../state/orgs";
import { useProbeInterviews } from "../apps/probe/probeInterviewsStore";
import { homeThreadState, selectOrgThreads } from "../home/homeLogic";
import { InspectorIcon } from "../InspectorIcon";
import { useFindingItems } from "../regulation/finding/useFindingItems";
import { AmoregMark } from "../sidebar/SidebarChrome";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import {
  automationNextRun,
  automationSummary,
  chatsThisWeek,
  inspectorCadence,
  inspectorLastRun,
  inspectorOpenFindings,
  relativeFuture,
} from "./agentsLogic";

const PENDING = "…";

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

const day = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });

/** The square mark each card leads with, on the card's own quiet tint. */
function AgentMark({
  children,
  className,
}: {
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "grid size-10 shrink-0 place-items-center rounded-xl border border-border/60 bg-muted/60 text-foreground [&>svg]:size-[18px]",
        className,
      )}
    >
      {children}
    </span>
  );
}

/** A state chip beside the name: "2 working", "Running", "Off". */
function StateChip({
  tone,
  children,
}: {
  readonly tone: "live" | "quiet";
  readonly children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-5 items-center gap-1.5 rounded-full px-2 text-[11px] font-medium",
        tone === "live" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
      )}
    >
      {tone === "live" ? (
        <span aria-hidden className="size-1.5 rounded-full bg-current motion-safe:animate-pulse" />
      ) : null}
      {children}
    </span>
  );
}

function Stat({
  label,
  value,
  note,
  href,
}: {
  readonly label: string;
  readonly value: ReactNode;
  readonly note?: ReactNode;
  readonly href?: ReactNode;
}) {
  return (
    <div className="min-w-0 bg-card px-4 py-3.5">
      <dt className="text-[12px] text-muted-foreground">{label}</dt>
      <dd className="mt-1 truncate text-[20px] font-semibold leading-tight tracking-[-0.02em] text-foreground tabular-nums">
        {href ?? value}
      </dd>
      {note ? <dd className="mt-0.5 truncate text-[12px] text-muted-foreground">{note}</dd> : null}
    </div>
  );
}

function AgentCard({
  mark,
  name,
  role,
  description,
  state,
  stats,
  footer,
  testId,
}: {
  readonly mark: ReactNode;
  readonly name: string;
  readonly role: string;
  readonly description: string;
  readonly state?: ReactNode;
  readonly stats: ReactNode;
  readonly footer: ReactNode;
  readonly testId: string;
}) {
  return (
    <section
      aria-label={name}
      data-agent-card={testId}
      className="flex min-w-0 flex-col rounded-2xl border border-border/60 bg-card shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
    >
      <div className="flex items-start gap-3.5 px-5 pt-5 sm:px-6 sm:pt-6">
        {mark}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h2 className="text-[15px] font-medium text-foreground">{name}</h2>
            <span className="text-[13px] text-muted-foreground">{role}</span>
            {state ? <span className="ms-auto">{state}</span> : null}
          </div>
          <p className="mt-1 text-[13px] leading-5 text-muted-foreground">{description}</p>
        </div>
      </div>
      <dl className="mx-5 mt-5 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border/60 bg-border/60 sm:mx-6 @[56rem]:grid-cols-[repeat(auto-fit,minmax(0,1fr))]">
        {stats}
      </dl>
      <footer className="mt-5 flex min-h-12 flex-wrap items-center justify-end gap-2 border-t border-border/60 px-5 py-2.5 sm:px-6">
        {footer}
      </footer>
    </section>
  );
}

function OpenLink({
  children,
  render,
}: {
  readonly children: ReactNode;
  readonly render: ReactElement;
}) {
  return (
    <Button size="sm" variant="ghost" className="gap-1.5" render={render}>
      {children}
      <ArrowRightIcon aria-hidden className="size-3.5" />
    </Button>
  );
}

/**
 * Probe's saved interviews, newest first — the Probe area's shared read:
 * `undefined` while the list loads, null when it is unavailable.
 */
function useProbeInterviewList(organizationId: string | null) {
  const { status, interviews } = useProbeInterviews(organizationId);
  return status === "loading" ? undefined : status === "error" ? null : interviews;
}

export function AgentsPage() {
  const org = useActiveOrg();
  const orgId = org?.id ?? null;
  const orgSegment = orgId ? orgUrlSegment(orgId) : null;
  const nowMinute = useNowMinute();
  const now = Date.parse(`${nowMinute}:00.000Z`) || Date.now();

  // Amoreg: the chats this person can see in the organization.
  const environmentId = usePrimaryEnvironmentId();
  const threads = useThreadShells();
  const projects = useProjects();
  const handleNewThread = useNewThreadHandler();
  const orgThreads = useMemo(
    () => selectOrgThreads(threads, { orgId, environmentId }),
    [environmentId, orgId, threads],
  );
  const orgProject = useMemo(
    () =>
      projects.find((project) => project.id === orgId && project.environmentId === environmentId) ??
      null,
    [environmentId, orgId, projects],
  );
  const weekChats = chatsThisWeek(orgThreads, now);
  const working = orgThreads.filter((thread) => homeThreadState(thread) === "working").length;
  const waiting = orgThreads.filter((thread) => homeThreadState(thread) === "waiting").length;

  // Inspector: its runs and booked run, the standing schedule, the findings it raised.
  const inspections = useInspections(orgId);
  const { profile, unavailableReason: profileUnavailable } = useOrgProfile(orgId);
  const findings = useFindingItems(orgId);
  const runs = inspections.snapshot?.runs ?? null;
  const runsUnavailable = runs === null && (inspections.error !== null || inspections.missing);
  const lastRun = runs ? inspectorLastRun(runs) : null;
  const cadence = inspectorCadence({
    schedule: profile?.inspectionSchedule ?? null,
    monitoringEnabled: profile?.regulatoryMonitoringEnabled ?? null,
    bookedAt: inspections.snapshot?.scheduled?.at ?? null,
    nowMs: now,
  });
  const openFindings = findings.ready ? inspectorOpenFindings(findings.items) : null;
  const findingsUnavailable = findings.unavailableReason !== null;
  const inspectorRunning =
    runs?.some((run) => run.status === "running" || run.status === "queued") ?? false;

  // Probe and automations.
  const interviews = useProbeInterviewList(orgId);
  const lastInterview = interviews?.[0] ?? null;
  const automations = useAutomations(orgId);
  const automationState =
    automations.isPending || automations.unavailableReason !== null
      ? null
      : automationSummary(automations.automations);
  const automationsUnavailable = automations.unavailableReason !== null;

  return (
    <ScrollArea className="h-full">
      <div
        data-agents-page=""
        className="@container mx-auto flex w-full max-w-[1240px] flex-col gap-5 px-4 pb-16 pt-14 sm:px-8 sm:pt-8"
      >
        <header className="min-w-0">
          <h1 className="text-balance text-[28px] font-semibold leading-tight tracking-[-0.02em] text-foreground">
            Agents
          </h1>
          <p className="mt-1 text-[13px] text-muted-foreground">
            {org
              ? `The agents that work for ${org.name}, and what each is doing.`
              : "The agents that work for you."}
          </p>
        </header>

        <div className="grid gap-5 @[56rem]:grid-cols-2">
          <AgentCard
            testId="amoreg"
            mark={
              <AgentMark className="bg-foreground text-background [&>svg]:h-4">
                <AmoregMark />
              </AgentMark>
            }
            name="Amoreg"
            role="Chat agent"
            description="Answers questions, drafts documents and works through compliance tasks, grounded in your regulators, policies and evidence."
            state={working > 0 ? <StateChip tone="live">{working} working</StateChip> : null}
            stats={
              <>
                <Stat label="Your chats this week" value={weekChats} note="Last 7 days" />
                <Stat
                  label="Waiting on you"
                  value={waiting}
                  note={waiting === 1 ? "A chat needs your answer" : "Approvals and questions"}
                />
              </>
            }
            footer={
              <>
                {orgSegment ? (
                  <OpenLink render={<Link to="/$org/chat" params={{ org: orgSegment }} />}>
                    All chats
                  </OpenLink>
                ) : null}
                <Button
                  size="sm"
                  className="gap-1.5"
                  disabled={orgProject === null}
                  data-agents-new-chat=""
                  onClick={() => {
                    if (!orgProject) return;
                    void handleNewThread(scopeProjectRef(orgProject.environmentId, orgProject.id));
                  }}
                >
                  <SquarePenIcon aria-hidden className="size-3.5" />
                  New chat
                </Button>
              </>
            }
          />

          <AgentCard
            testId="inspector"
            mark={
              <AgentMark>
                <InspectorIcon ink="inherit" />
              </AgentMark>
            }
            name="Inspector"
            role="Review agent"
            description="Reads your library against your regulators and raises findings. It proposes; your team decides."
            state={
              inspectorRunning ? (
                <StateChip tone="live">Running</StateChip>
              ) : cadence ? (
                <StateChip tone="quiet">{cadence.label}</StateChip>
              ) : null
            }
            stats={
              <>
                <Stat
                  label={cadence?.booked ? "Booked run" : "Next run"}
                  value={
                    cadence === null
                      ? profileUnavailable
                        ? "—"
                        : PENDING
                      : cadence.nextAt
                        ? relativeFuture(cadence.nextAt.getTime(), now)
                        : "None"
                  }
                  note={
                    cadence?.nextAt
                      ? when(cadence.nextAt.toISOString())
                      : cadence
                        ? "Nothing on the calendar"
                        : undefined
                  }
                />
                <Stat
                  label="Last run"
                  value={
                    runs === null
                      ? runsUnavailable
                        ? "—"
                        : PENDING
                      : lastRun
                        ? when(lastRun.at)
                        : "Never"
                  }
                  note={lastRun?.result}
                  href={
                    lastRun && orgSegment ? (
                      <Link
                        to="/$org/compliance/inspector/$threadId"
                        params={{ org: orgSegment, threadId: lastRun.run.threadId }}
                        className="underline-offset-4 hover:underline"
                      >
                        {when(lastRun.at)}
                      </Link>
                    ) : undefined
                  }
                />
                <Stat
                  label="Open findings"
                  value={openFindings ?? (findingsUnavailable ? "—" : PENDING)}
                  note={
                    openFindings === null
                      ? undefined
                      : openFindings === 0
                        ? "None open"
                        : "Raised by Inspector"
                  }
                />
              </>
            }
            footer={
              orgSegment ? (
                <OpenLink
                  render={<Link to="/$org/compliance/inspector" params={{ org: orgSegment }} />}
                >
                  Open Inspector
                </OpenLink>
              ) : null
            }
          />

          <AgentCard
            testId="probe"
            mark={
              <AgentMark className="bg-primary/10 text-primary">
                <RadarIcon />
              </AgentMark>
            }
            name="Probe"
            role="Interview app"
            description="Rehearses the regulator's interview by voice, grounded in your policies, and keeps each transcript."
            stats={
              <Stat
                label="Last interview"
                value={
                  interviews === undefined
                    ? PENDING
                    : interviews === null
                      ? "—"
                      : lastInterview
                        ? day(lastInterview.createdAt)
                        : "None yet"
                }
                note={
                  lastInterview
                    ? lastInterview.title
                    : interviews === null
                      ? "History is not available on this server"
                      : interviews === undefined
                        ? undefined
                        : "Start one from Probe"
                }
              />
            }
            footer={
              orgSegment ? (
                <OpenLink
                  render={
                    <Link to="/$org/apps/probe" params={{ org: orgSegment }} />
                  }
                >
                  Open Probe
                </OpenLink>
              ) : null
            }
          />

          <AgentCard
            testId="automations"
            mark={
              <AgentMark>
                <CalendarClockIcon />
              </AgentMark>
            }
            name="Automations"
            role="Scheduled agents"
            description="Your prompts and approved sends, run on a schedule in this organization."
            stats={
              <>
                <Stat
                  label="Active"
                  value={
                    automationState === null
                      ? automationsUnavailable
                        ? "—"
                        : PENDING
                      : automationState.active
                  }
                  note={
                    automationState === null
                      ? automationsUnavailable
                        ? "Not available on this server"
                        : undefined
                      : `${automationState.total} ${automationState.total === 1 ? "automation" : "automations"} in all`
                  }
                />
                <Stat
                  label="Next run"
                  value={automationNextRun(automationState, automationsUnavailable, now)}
                  note={automationState?.next ? automationState.next.title : undefined}
                />
              </>
            }
            footer={
              orgSegment ? (
                <OpenLink render={<Link to="/$org/automations" params={{ org: orgSegment }} />}>
                  Open automations
                </OpenLink>
              ) : null
            }
          />
        </div>
      </div>
    </ScrollArea>
  );
}
